'use client'

import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ReactElement, ReactNode } from 'react'
import { cn } from '../../utils/cn'
import { spinnerVariants, type SpinnerProps } from '../feedback/spinner'
import { Icon, type IconProps } from '../icons/icon'
import { renderAs } from '../internal/render-as'

/**
 * Structure from shadcn, variants from the button that has actually met users.
 *
 * Every colour is a token-backed utility. There is no `className` or `style`
 * prop: a caller that needs a look the variants do not cover adds a variant here
 * with the designer, rather than leaking a one-off style into an app. That is
 * the whole point of the package, and it is enforced by the type, not by review.
 */
export const buttonVariants = cva(
    [
        'inline-flex items-center justify-center gap-2 whitespace-nowrap',
        'border font-normal',
        // Every glyph inside takes the button's colour, whether it arrived
        // through iconLeft or as a child. Icon stamps a `text-*` of its own from
        // its tone, and a descendant selector is what outranks it, so a caller
        // cannot leave a dark glyph on a filled button by accident.
        '[&_svg]:text-inherit',
        'transition-colors duration-200',
        'cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:bg-background-disabled disabled:text-foreground-disabled disabled:border-border'
    ],
    {
        variants: {
            intent: {
                primary:
                    // The designer's list has no brand hover or pressed surface. Their
                    // migration map calls for an alpha on the background rather than
                    // a new token, which also leaves the label at full opacity.
                    'bg-background-brand text-foreground-brand border-border-brand hover:bg-background-brand/90 active:bg-background-brand/80',
                brand: 'bg-background-success-strong text-foreground-onEmphasis border-border-success-strong hover:bg-background-success-strong/90',
                secondary: 'bg-background text-foreground border-border hover:bg-background-hover',
                /**
                 * `danger` rather than `negative` for the red.
                 *
                 * The word is the one the adopted token list uses
                 * (`--color-background-danger`), and Badge, Icon, Paragraph and
                 * DataRow all already say it. A second name for one colour is
                 * how a package ends up speaking two languages.
                 */
                danger: 'bg-background-danger text-foreground-danger border-border-danger hover:border-border-danger-strong',
                /**
                 * The green tint, not the filled green.
                 *
                 * `brand` is the filled one. This is the surface that pairs with
                 * `danger` and `warning`, for an action that confirms in place
                 * without claiming to be the page's primary action.
                 */
                success:
                    'bg-background-success text-foreground-success border-border-success hover:border-border-success-strong',
                warning:
                    'bg-background-warning text-foreground-warning border-border-warning hover:border-border-warning-strong',
                /** The blue tint, for an action that reports rather than warns. */
                info: 'bg-background-info text-foreground-info border-border-info hover:border-border-info-strong',
                ghost: 'bg-transparent text-foreground border-transparent hover:bg-background-hover',
                link: 'bg-transparent text-foreground-accent border-transparent hover:underline',
                /**
                 * A control with no look of its own.
                 *
                 * For a trigger whose content is already a designed thing: a
                 * status badge that opens a menu, a row that opens a drawer.
                 * `ghost` still draws a box and a hover surface, and `link`
                 * recolours and underlines, so both fight the content. This
                 * one only carries the behaviour and the focus ring.
                 */
                plain: 'bg-transparent text-foreground border-transparent hover:text-foreground-muted'
            },
            size: {
                // `leading-none` sits with each size rather than in the base,
                // because a Tailwind font-size utility carries its own
                // line-height and the merge keeps whichever comes last.
                small: 'h-6 px-2 text-aria-xs leading-none rounded-aria-sm',
                medium: 'h-8 px-3 text-aria-base leading-none rounded-aria-md',
                large: 'h-10 px-4 text-aria-lg leading-none rounded-aria-md',
                square: 'size-8 p-0 leading-none rounded-aria-md'
            },
            isFullWidth: {
                true: 'w-full',
                false: ''
            }
        },
        // Neither `link` nor `plain` has a box, so both undo whatever `size` set.
        // cva emits compound classes last, which is what makes the merge in the
        // component resolve this in their favour rather than by stylesheet order.
        //
        // `square` is excluded on purpose. It is the one size whose whole job is
        // to be a box, for a glyph-only control, and undoing it collapsed an
        // icon-only menu trigger to the glyph.
        compoundVariants: [
            {
                intent: ['link', 'plain'],
                size: ['small', 'medium', 'large'],
                class: 'h-auto w-fit p-0'
            }
        ],
        defaultVariants: {
            intent: 'primary',
            size: 'medium',
            isFullWidth: false
        }
    }
)

type ButtonVariants = VariantProps<typeof buttonVariants>

/**
 * `isLoading` is a state, not a component.
 *
 * Both donors wrapped this in a `LoadingButton`, 646 references across 161
 * files, and a second Button that differs by one boolean is how a set grows a
 * variant nobody can find.
 *
 * Everything on a prop's own JSDoc below is consumer-facing: it is what an
 * editor shows on autocomplete and what the gallery prints as the reference. The
 * reasoning behind a prop belongs here, where whoever edits this file reads it.
 */
export type ButtonProps = Omit<ButtonPrimitive.Props, 'className' | 'style' | 'render'> &
    ButtonVariants & {
        /**
         * Renders your own element with the button's styling.
         *
         * An anchor stays a real link, so it keeps middle click, open in a new
         * tab and copy link address.
         */
        render?: ReactElement<{ children?: ReactNode }>
        /**
         * Disables the button, announces the busy state, and swaps the label for
         * a spinner.
         *
         * The label keeps its space rather than unmounting, so the button does
         * not resize under the pointer that just pressed it.
         */
        isLoading?: boolean
        /**
         * A glyph before the label, sized and coloured by the button.
         *
         * Decorative: the label names the action, so the icon is hidden from
         * assistive technology. A button whose only content is a glyph is
         * `size="square"` with an `aria-label`, not this.
         */
        iconLeft?: IconProps['icon']
        /**
         * A glyph after the label, sized and coloured by the button.
         *
         * For a direction or a disclosure, such as the chevron on a step that
         * moves the flow forward. Decorative, like `iconLeft`.
         */
        iconRight?: IconProps['icon']
    }

/**
 * The size a glyph takes for each button size.
 *
 * Both maps are keyed by `ButtonSize` rather than by their own literals, so
 * adding a size to the cva block fails to compile until both are extended. The
 * three lists cannot drift apart by hand.
 */
type ButtonSize = NonNullable<ButtonVariants['size']>

/** The busy glyph is sized from the button, not from the Spinner's own scale. */
const SPINNER_SIZE: Record<ButtonSize, NonNullable<SpinnerProps['size']>> = {
    small: 'small',
    medium: 'medium',
    large: 'large',
    square: 'medium'
}

/**
 * An icon is sized from the button too, for the same reason.
 *
 * A caller choosing the glyph size is how two buttons side by side end up with
 * icons that do not line up, so the scale is not a prop.
 */
const ICON_SIZE: Record<ButtonSize, NonNullable<IconProps['size']>> = {
    small: 1,
    medium: 2,
    large: 3,
    square: 2
}

export const Button = ({
    intent,
    size,
    isFullWidth,
    isLoading = false,
    disabled,
    type = 'button',
    render,
    iconLeft,
    iconRight,
    ...props
}: ButtonProps) => {
    const className = cn(buttonVariants({ intent, size, isFullWidth }), isLoading && 'relative')

    const resolvedSize = size ?? 'medium'
    const hasIcon = Boolean(iconLeft || iconRight)

    /**
     * The glyphs sit around the label rather than beside it, so `isLoading` has
     * one thing to hide and the reserved width covers the icons too. `tone` is
     * `inherit` because the intent, the hover and the disabled state all set a
     * text colour on the button, and the glyph has to follow every one of them.
     */
    const withIcons = (label: ReactNode) =>
        hasIcon ? (
            <>
                {iconLeft ? <Icon icon={iconLeft} tone="inherit" size={ICON_SIZE[resolvedSize]} /> : null}
                {label}
                {iconRight ? <Icon icon={iconRight} tone="inherit" size={ICON_SIZE[resolvedSize]} /> : null}
            </>
        ) : (
            label
        )

    // A rendered element keeps its own semantics. Handing it to Base UI's Button
    // instead would put role="button" on an anchor, and `disabled` means nothing
    // on one, so neither is passed on.
    if (render) {
        // Children are replaced only when there is a glyph to place, so a
        // caller's own element keeps the exact nodes it was given otherwise.
        // `render={<a>Invoices</a>}` has its label in the element, not in props.
        const label = props.children ?? render.props.children

        return renderAs(render, {
            ...props,
            // The key is absent, not undefined, when there is no glyph: setting
            // it either way would drop children the caller did pass.
            ...(hasIcon && { children: withIcons(label) }),
            className,
            'aria-busy': isLoading || undefined
        })
    }

    const { children, ...rest } = props

    return (
        <ButtonPrimitive
            type={type}
            disabled={disabled || isLoading}
            aria-busy={isLoading || undefined}
            className={className}
            {...rest}
        >
            {isLoading ? (
                <>
                    {/* Hidden rather than unmounted: the label still reserves
                        its width, so the button does not resize mid-press. The
                        wrapper repeats the button's own flex row, because the
                        gaps between the glyphs and the label are part of that
                        width and a plain span would drop them. */}
                    <span className="invisible inline-flex items-center gap-2">{withIcons(children)}</span>
                    <span
                        // The button already carries aria-busy, so the glyph is
                        // decorative. A Spinner here would add a second live
                        // region inside a control that has already announced.
                        aria-hidden
                        className={cn(
                            spinnerVariants({ size: SPINNER_SIZE[resolvedSize], tone: 'inherit' }),
                            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
                        )}
                    />
                </>
            ) : (
                withIcons(children)
            )}
        </ButtonPrimitive>
    )
}
